import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import api from '../../services/api';
import { useModal } from '../../contexts/ModalContext';
import { useLoader } from '../../contexts/LoaderContext';
import { useAccount } from '../../contexts/AccountContext';
import TeamMembersManagement from '../../components/teams/TeamMembersManagement';

const TeamMembers = ({ users, teams, userroles, onEdit, confirmUserDelete, currentUser, onRefresh, whatsappSettings }) => {
  const { openErrorModal, openWarningModal, openSuccessModal } = useModal();
  const { showLoader, hideLoader } = useLoader();
  const { accountId } = useAccount();
  const [editUser, setEditUser] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // Handle editing user
  const handleEditUserClick = (user) => {
    // For MG users, only allow editing users from their own team
    if (currentUser?.role === 'MG' && user.team_id !== currentUser.team_id) {
      openErrorModal('You can only edit members from your own team');
      return;
    }

    setEditUser(user);
    setOpenEditDialog(true);
  };

  // Handle assigning user to team
  const handleAssignTeam = async (userId, teamId) => {
    try {
      showLoader("Updating team assignment...");
      
      // Format the payload - teamId can be empty string for unassigning
      const payload = {
        team_id: teamId || null
      };
      
      await api.patch(`account/user/${accountId}/${userId}/`, payload);
      
      // Refresh user data
          await onRefresh();

      openSuccessModal('Success', 'Team assignment updated successfully');
      return true;
    } catch (error) {
      console.error('Error assigning team:', error);
      
      if (error.response?.status === 400) {
        openErrorModal('Error', error.response.data.message || 'Invalid data');
      } else {
        openErrorModal('Error', 'Failed to update team assignment');
      }
      
      return false;
    } finally {
      hideLoader();
    }
  };

  // Handle toggling lead manager status
  const handleToggleLeadManager = async (userId, isLeadManager) => {
    try {
      showLoader("Updating lead manager status...");
      
      const payload = {
        is_lead_manager: isLeadManager
      };
      
      await api.post(`account/update-lead-manager/${userId}/`, payload);
      
      // Refresh user data
      await onRefresh();
      
      openSuccessModal('Success', `Lead manager status ${isLeadManager ? 'enabled' : 'disabled'}`);
      return true;
    } catch (error) {
      console.error('Error updating lead manager status:', error);
      
      if (error.response?.status === 400) {
        openErrorModal('Error', error.response.data.message || 'Invalid data');
      } else {
        openErrorModal('Error', 'Failed to update lead manager status');
      }
      
      return false;
    } finally {
      hideLoader();
    }
  };

  // Handle adding a new user
  const handleAddUser = async (userData) => {
    try {
      showLoader("Adding new user...");
      
      const response = await api.post(`account/users/${accountId}/`, userData);
      
      // Refresh user data
      await onRefresh();
      
      openSuccessModal('Success', 'New team member added successfully');
      return true;
    } catch (error) {
      console.error('Error adding user:', error);
      
      if (error.response?.status === 400) {
        // Extract field errors from response
        const errors = error.response.data;
        setFieldErrors(errors);
        
        // Display general error message
        openErrorModal('Error', 'Please correct the errors in the form');
      } else {
        openErrorModal('Error', 'Failed to add new user');
      }
      
      return false;
    } finally {
      hideLoader();
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <TeamMembersManagement
        users={users}
        teams={teams}
        currentUser={currentUser}
        onEdit={onEdit} // Pass the onEdit prop received from parent
        onRemove={confirmUserDelete}
        onAssign={handleAssignTeam}
        onAddMember={handleAddUser}
        onToggleLeadManager={handleToggleLeadManager}
        openEditDialog={openEditDialog} // Pass state to control dialog visibility
        setOpenEditDialog={setOpenEditDialog} // Pass setter to control dialog visibility
        editUser={editUser} // Pass user data to edit
        setEditUser={setEditUser} // Pass setter for user data
        userroles={userroles} // Pass userroles to TeamMembersManagement
        whatsappSettings={whatsappSettings}
      />
      
      {/* Legacy Edit Dialog and other functionality can remain here */}
      {/* You can gradually phase out the old implementation as needed */}
    </Box>
  );
};

export default TeamMembers;